import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useFetch from "hooks/useFetch";
import {
  deviceDescription,
  formatAgo,
  isOnline,
  statusBadge,
  TvDevice,
} from "pages/TvPage/tvDevices";
import {
  Badge,
  Button,
  Card,
  Column,
  ConfirmDialog,
  EmptyState,
  Icon,
  PageHeader,
  Skeleton,
  Table,
  TextField,
  Toggle,
} from "stories/Admin";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import PageModal from "../LocationsPage/PageModal";
import styles from "./TvDevicesPage.module.scss";

/*
 * Superadmin: alle TV-er moskeene har koblet til med kode, hvem som la dem til og når de sist var aktive.
 */

const TvDevicesPage: React.FC = () => {
  const { execute } = useFetch<any>();
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const [devices, setDevices] = useState<TvDevice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showRevoked, setShowRevoked] = useState(false);
  const [details, setDetails] = useState<TvDevice | null>(null);
  const [revoking, setRevoking] = useState<TvDevice | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    const result = await executeRef.current("/api/v1/admin/tv-devices", "GET");
    setDevices(Array.isArray(result) ? result : []);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return devices.filter(
      (d) =>
        (showRevoked || d.status !== "revoked") &&
        (!q ||
          (d.mosqueName || "").toLowerCase().includes(q) ||
          d.orgNr.includes(q) ||
          d.name.toLowerCase().includes(q) ||
          (d.createdByEmail || "").toLowerCase().includes(q)),
    );
  }, [devices, search, showRevoked]);

  const active = devices.filter((d) => d.status === "active");
  const online = active.filter((d) => isOnline(d)).length;

  const revoke = async () => {
    if (!revoking) return;
    await executeRef.current(
      `/api/v1/admin/tv-devices/${revoking.id}`,
      "DELETE",
    );
    setRevoking(null);
    setDetails(null);
    load();
  };

  const columns: Array<Column<TvDevice>> = [
    {
      key: "mosque",
      header: "Moské",
      render: (d) => (
        <span className={styles.stack}>
          <b>{d.mosqueName || d.orgNr}</b>
          <span className={styles.muted}>{d.orgNr}</span>
        </span>
      ),
    },
    {
      key: "tv",
      header: "TV",
      render: (d) => (
        <span className={styles.stack}>
          <span>{d.name}</span>
          {deviceDescription(d) && (
            <span className={styles.muted}>{deviceDescription(d)}</span>
          )}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (d) => {
        const badge = statusBadge(d);
        return <Badge tone={badge.tone}>{badge.label}</Badge>;
      },
    },
    {
      key: "by",
      header: "Lagt til av",
      render: (d) => (
        <span className={styles.stack}>
          <span>{d.createdByEmail || "–"}</span>
          <span className={styles.muted}>{formatAgo(d.createdAt)}</span>
        </span>
      ),
    },
    {
      key: "last",
      header: "Sist aktiv",
      numeric: true,
      render: (d) => formatAgo(d.lastSeenAt),
    },
  ];

  const stats: Array<[string, React.ReactNode]> = [
    ["Tilkoblede TV-er", isLoading ? "–" : active.length],
    [
      "Moskeer med TV",
      isLoading ? "–" : new Set(active.map((d) => d.orgNr)).size,
    ],
    ["På nå", isLoading ? "–" : online],
    [
      "Venter på kode",
      isLoading ? "–" : devices.filter((d) => d.status === "pending").length,
    ],
  ];

  const detailRows: Array<[string, React.ReactNode]> = details
    ? [
        ["Status", statusBadge(details).label],
        [
          "Lagt til",
          `${formatAgo(details.createdAt)} av ${details.createdByEmail || "ukjent"}`,
        ],
        ["Koblet til", formatAgo(details.activatedAt)],
        ["Sist aktiv", formatAgo(details.lastSeenAt)],
        ["Siste IP", details.lastIp || "–"],
        ["TV-en", deviceDescription(details) || "–"],
        ["Klient", details.lastUserAgent || "–"],
        ["Token", details.tokenPrefix ? `${details.tokenPrefix}…` : "–"],
        ...(details.code
          ? ([["Kode", details.code]] as Array<[string, React.ReactNode]>)
          : []),
        ...(details.revokedAt
          ? ([["Koblet fra", formatAgo(details.revokedAt)]] as Array<
              [string, React.ReactNode]
            >)
          : []),
      ]
    : [];

  return (
    <>
      <PageHeader
        title="TV-skjermer"
        subtitle="Alle TV-er moskeene har koblet til med kode: hvem som la dem til, og når de sist var aktive."
        actions={
          <Button
            variant="secondary"
            icon={<Icon name="swap" size={16} />}
            onClick={load}
            loading={isLoading}
          >
            Oppdater
          </Button>
        }
      />
      <SectionTabs />

      <div className={styles.stats}>
        {stats.map(([label, value]) => (
          <div key={label} className={styles.stat}>
            <b>{value}</b>
            <span>{label}</span>
          </div>
        ))}
      </div>

      <div className={styles.filters}>
        <TextField
          aria-label="Søk"
          type="search"
          placeholder="Søk etter moské, org.nr., TV-navn eller e-post"
          className={styles.search}
          fieldClassName={styles.searchField}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Toggle
          id="vis-frakoblede"
          label="Vis frakoblede"
          checked={showRevoked}
          onChange={(e) => setShowRevoked(e.target.checked)}
        />
      </div>

      <Card className={styles.tableCard}>
        {isLoading && devices.length === 0 ? (
          <div className={styles.skeletons}>
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} height={40} />
            ))}
          </div>
        ) : (
          <Table
            columns={columns}
            rows={rows}
            rowKey={(d) => d.id}
            onRowClick={(d) => setDetails(d)}
            rowState={(d) => (d.status === "revoked" ? "dim" : undefined)}
            empty={<EmptyState icon="tv" title="Ingen TV-er ennå" />}
          />
        )}
      </Card>

      <PageModal
        open={!!details}
        title={`${details?.mosqueName || details?.orgNr} – ${details?.name}`}
        icon="tv"
        width={640}
        onClose={() => setDetails(null)}
        footer={
          <>
            {details && details.status !== "revoked" && (
              <Button
                variant="danger"
                icon={<Icon name="trash" size={16} />}
                onClick={() => setRevoking(details)}
              >
                Koble fra TV-en
              </Button>
            )}
            <Button variant="secondary" onClick={() => setDetails(null)}>
              Lukk
            </Button>
          </>
        }
      >
        <dl className={styles.details}>
          {detailRows.map(([label, value]) => (
            <React.Fragment key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </React.Fragment>
          ))}
        </dl>
      </PageModal>

      <ConfirmDialog
        open={!!revoking}
        title="Koble fra TV-en?"
        tone="danger"
        confirmLabel="Koble fra"
        onConfirm={revoke}
        onCancel={() => setRevoking(null)}
      >
        «{revoking?.name}» for {revoking?.mosqueName || revoking?.orgNr} mister
        tilgangen med en gang.
      </ConfirmDialog>
    </>
  );
};

export default TvDevicesPage;
